import axios, { AxiosResponse } from 'axios';
import { PythonFilesAPI } from '../services';
import { FILE_EXTENSIONS } from 'components/utils/constants';
import { AttachmentType } from '@buildwise/pages/knowledge/hooks/useArticlesQuery';
import { attachFiles } from 'utility/helpers';

// Appends a fallback extension if the file name lacks a valid one.
export const getFileNameWithExtension = ({
  fileName, // File name to check
  fallbackExtension = 'jpeg', // Default extension
}: {
  fileName: string;
  fallbackExtension?: (typeof FILE_EXTENSIONS)[number];
}): string => {
  const extensionFromFileName = fileName.split('.').pop() ?? '';

  return (FILE_EXTENSIONS as readonly string[]).includes(extensionFromFileName)
    ? fileName
    : `${fileName}.${fallbackExtension}`;
};

/**
 * Downloads a list of files from given URLs, handles proxy retrieval if needed,
 * and returns attachments.
 *
 * @param {Object} params
 * @param {{ url: string }[]} params.urls - List of file URLs to download.
 * @returns List of downloaded attachments.
 */
export const downloadFilesWithUrls = async ({
  urls,
}: {
  urls: { url: string; fileName: string }[];
}) => {
  const attachments: AttachmentType[] = [];
  const filePromises: Promise<void>[] = [];
  const files: File[] = [];

  urls.forEach(({ url, fileName: _fileName }, index) => {
    filePromises.push(
      new Promise(async (resolve, reject) => {
        try {
          let response: AxiosResponse<Blob>;

          // Attempt direct download; fallback to proxy if necessary
          try {
            response = await axios(url, { responseType: 'blob' });
          } catch {
            const { data: s3Url } = await PythonFilesAPI.getExternalFileUrlWithProxyAPI(url);
            response = await axios.get(s3Url, { responseType: 'blob' });
          }

          if (response?.data) {
            const contentDisposition = response.headers['content-disposition'];
            const defaultFileName =
              new URL(url).pathname.split('/').pop() || `Untitled-${index + 1}`;

            // Extract filename from Content-Disposition header if available
            const fileNameMatch = contentDisposition?.match(/filename="(.+)"/);
            const fileName = fileNameMatch?.[1] || defaultFileName;

            const blobType = (FILE_EXTENSIONS as readonly string[]).includes(response.data.type)
              ? response.data.type
              : 'image/jpeg';
            const blob = new Blob([response.data], { type: blobType });

            files.push(
              new File([blob], getFileNameWithExtension({ fileName: _fileName || fileName }), {
                type: blob.type,
              }),
            );
            resolve();
          } else {
            reject(new Error('No data in response'));
          }
        } catch (error) {
          reject(error);
        }
      }),
    );
  });

  try {
    await Promise.all(filePromises);

    // Attach files and update attachments array
    await attachFiles({
      newAttachments: files,
      attachments: [],
      setAttachments: (_attachments) => {
        attachments.push(..._attachments);
      },
    });
  } catch (error) {
    console.error('Error downloading files:', error);
  }

  return attachments;
};
